import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useI18n } from '@/i18n/i18n-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useTaktikalAuth } from '@/hooks/taktikal-auth'
import { useState } from 'react'
import { TESTER_PHONE, turnOnTesterMode, isLoginRequired } from '@/utils/tester-mode'
import { localeSubject } from '@/providers/root-providers'
import { ArrowLeft } from 'lucide-react'
import { z } from 'zod'

export const Route = createFileRoute('/onboarding/login')({
	validateSearch: z.object({ redirect: z.string().optional() }),
	component: LoginScreen
})

function LoginScreen() {
	const { L } = useI18n()
	const navigate = useNavigate()
	const { redirect } = Route.useSearch()
	const [authMethod, setAuthMethod] = useState<'phone' | 'ssn'>('phone')
	const [value, setValue] = useState('')

	const onComplete = () => {
		navigate({ to: '/onboarding/intro', search: { redirect } })
	}

	const { verificationCode, isAuthenticating, isTimedOut, isPending, error, handleSignIn } = useTaktikalAuth({
		authMethod,
		value,
		onSuccess: onComplete
	})

	const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		setValue(e.target.value.replace(/[^0-9+]/g, ''))
	}

	const handleLogin = () => {
		if (authMethod === 'phone' && value === TESTER_PHONE) {
			turnOnTesterMode()
			localeSubject.next('en')
			navigate({ to: '/onboarding/intro', search: { redirect } })
			return
		}
		handleSignIn()
	}

	return (
		<div className='bg-primary flex min-h-dvh flex-col'>
			<div className='flex items-center px-4 pt-4'>
				<Button
					variant='ghost'
					size='icon'
					className='text-primary-foreground hover:bg-primary-foreground/10'
					onClick={() => navigate({ to: '/onboarding/welcome' })}
				>
					<ArrowLeft className='h-5 w-5' />
				</Button>
			</div>

			<div className='flex flex-1 flex-col gap-6 px-6 py-8'>
				<div className='text-center'>
					<h1 className='text-primary-foreground text-2xl font-bold'>{L.Onboarding.LoginTitle()}</h1>
					<p className='text-primary-foreground/80 mt-2'>{L.Onboarding.LoginSubtitle()}</p>
				</div>

				{/* Method toggle */}
				<div className='border-primary-foreground/30 flex rounded-lg border p-1'>
					<Button
						variant='ghost'
						className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
							authMethod === 'phone' ?
								'bg-primary-foreground text-primary hover:bg-primary-foreground hover:text-primary'
							:	'text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground'
						}`}
						onClick={() => {
							setAuthMethod('phone')
							setValue('')
						}}
					>
						{L.Auth.TaktikalPhoneMethod()}
					</Button>
					<Button
						variant='ghost'
						className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
							authMethod === 'ssn' ?
								'bg-primary-foreground text-primary hover:bg-primary-foreground hover:text-primary'
							:	'text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground'
						}`}
						onClick={() => {
							setAuthMethod('ssn')
							setValue('')
						}}
					>
						{L.Auth.TaktikalSsnMethod()}
					</Button>
				</div>

				<Input
					type='tel'
					inputMode='numeric'
					placeholder={authMethod === 'phone' ? L.Auth.PhoneNumber() : L.Auth.SSN()}
					value={value}
					onChange={handleInput}
					onKeyDown={e => e.key === 'Enter' && handleLogin()}
					className='border-primary-foreground/50 bg-primary-foreground/10 text-primary-foreground placeholder:text-primary-foreground/50'
				/>

				{verificationCode && (
					<p className='text-primary-foreground/80 text-center text-sm'>
						{L.Auth.VerificationCode(verificationCode)}
					</p>
				)}

				{error && (
					<div className='bg-destructive/20 text-primary-foreground rounded-lg p-3 text-center text-sm'>
						{error}
					</div>
				)}
			</div>

			<div className='flex gap-3 p-6'>
				{!isLoginRequired() && (
					<Button
						variant='ghost'
						className='text-primary-foreground hover:bg-primary-foreground/10 flex-1'
						onClick={() => navigate({ to: '/onboarding/intro', search: { redirect } })}
					>
						{L.Common.Actions.Skip?.() ?? 'Skip'}
					</Button>
				)}
				<Button
					className='bg-primary-foreground text-primary hover:bg-primary-foreground/90 flex-1'
					onClick={handleLogin}
					disabled={!value.trim() || (isAuthenticating && !isTimedOut)}
					isLoading={isPending || isAuthenticating}
				>
					{isTimedOut && isAuthenticating ? L.Auth.Retry() : L.Auth.SignIn()}
				</Button>
			</div>
		</div>
	)
}
